/**
 * The print design system.
 *
 * Screen styling and print styling are different problems. On screen a colour
 * can shift with the theme; on paper it is ink, and the browser will silently
 * drop a fill unless told otherwise. Everything here exists to make printed
 * output deliberate: fixed A4 geometry and a palette derived from the parish's
 * own brand colour.
 *
 * Templates describe *shape*. This file describes *material*.
 *
 * The page CSS that used to live here printed by hiding the rest of the
 * application — `body * { visibility: hidden }` around a scope class. That is
 * what put whole screens on paper and dark-mode text on white sheets, so it is
 * gone: `printService` now opens a standalone document instead.
 */

// ---------------------------------------------------------------------------
// Page geometry
// ---------------------------------------------------------------------------

/** A4 at 96dpi. Used so previews on screen match what comes out of a printer. */
export const A4 = {
  widthMm: 210,
  heightMm: 297,
  widthPx: 794,
  heightPx: 1123
};

export type PageOrientation = 'portrait' | 'landscape';

// ---------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------

const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));

const parseHex = (hex: string): [number, number, number] => {
  const clean = String(hex || '').replace('#', '').trim();
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean.padEnd(6, '0').slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const toHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('')}`;

export const rgba = (hex: string, alpha: number): string => {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const lighten = (hex: string, amount: number): string => {
  const [r, g, b] = parseHex(hex);
  return toHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
};

export const darken = (hex: string, amount: number): string => {
  const [r, g, b] = parseHex(hex);
  return toHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
};

/**
 * Perceived brightness, so text placed on the accent stays readable whatever
 * colour the parish picks. A pale yellow band needs black text; a navy one
 * needs white. Guessing wrong makes a certificate unreadable.
 */
export const readableOn = (hex: string): string => {
  const [r, g, b] = parseHex(hex);
  return (r * 299 + g * 587 + b * 114) / 1000 > 145 ? '#111111' : '#FFFFFF';
};

/** The full ink palette a template draws from, derived from one brand colour. */
export interface PrintPalette {
  accent: string;
  accentDark: string;
  accentLight: string;
  accentPale: string;
  /** Readable text colour when sitting directly on `accent`. */
  onAccent: string;
  ink: string;
  inkSoft: string;
  inkFaint: string;
  rule: string;
  ruleSoft: string;
  paper: string;
}

export const buildPalette = (brandColour: string): PrintPalette => {
  const accent = brandColour || '#6B4277';
  return {
    accent,
    accentDark: darken(accent, 0.28),
    accentLight: lighten(accent, 0.55),
    accentPale: lighten(accent, 0.88),
    onAccent: readableOn(accent),
    ink: '#111111',
    inkSoft: '#444444',
    inkFaint: '#777777',
    rule: '#333333',
    ruleSoft: '#DDDDDD',
    paper: '#FFFFFF'
  };
};
